<script setup lang="ts">
    import { computed, inject } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { chatbotContextKey } from './constants'
    import type { Chat as Conversation, UIChatMessage } from 'models'
    import { stripMarkdown } from 'utils'
    import PkRelativeTime from '../PkRelativeTime.vue'

    const { t: $t } = useI18n({ useScope: 'global' })
    const ctx = inject(chatbotContextKey)!

    const conversations = computed(() => ctx.conversations.value)

    const openConversation = (id: string) => {
        ctx.localChatId.value = id
        ctx.navigate('chat')
    }

    const isCurrentConversation = (conversation: Conversation) => {
        return ctx.localChatId.value === conversation.id
    }

    const getLastMessage = (
        messages: UIChatMessage[],
        role: 'user' | 'assistant',
    ) => {
        return stripMarkdown(
            messages
                .filter((message) => message.role === role)
                .slice(-1)[0]
                ?.parts?.find((part) => part.type === 'text')?.text || '',
        )
    }
</script>

<template>
    <div
        class="flex flex-col flex-1 min-h-0 overflow-y-auto p-md gap-sm relative">
        <div
            v-if="conversations.length === 0"
            class="flex justify-center p-lg text-word-3 text-sm">
            {{ $t('message.noConversations') }}
        </div>
        <ul
            v-else
            class="flex flex-col min-h-0 gap-8 overflow-auto px-8 pb-8 light-scrollbar pb-64">
            <li v-for="conversation in conversations" :key="conversation.id">
                <button
                    type="button"
                    class="rounded-md border p-10 cursor-pointer block w-full transition-colors text-14 leading-relaxed border-b border-surface-3 hover:border-surface-5 hover:bg-surface-1"
                    :class="{
                        'bg-surface-2 border-surface-5':
                            isCurrentConversation(conversation),
                    }"
                    @click="openConversation(conversation.id)">
                    <div class="flex items-center gap-16">
                        <strong class="font-bold truncate block flex-1">
                            {{
                                stripMarkdown(conversation.title) ||
                                getLastMessage(
                                    conversation.messages,
                                    'assistant',
                                )
                            }}
                        </strong>
                        <span class="text-word-4 text-smaller shrink-0">
                            <PkRelativeTime :date="conversation.updatedAt" />
                        </span>
                    </div>
                    <span class="text-12 text-word-4 line-clamp-2">
                        {{
                            stripMarkdown(conversation.summary) ||
                            getLastMessage(conversation.messages, 'user')
                        }}
                    </span>
                </button>
            </li>
        </ul>
        <div
            class="absolute bottom-0 left-0 right-0 flex justify-center px-16 pb-16 pt-32 bg-gradient-to-t">
            <VvButton
                class="text-14"
                modifiers="rounded"
                :label="$t('action.startNewChat')"
                @click.stop="ctx.startNewChat()" />
        </div>
    </div>
</template>
